import { useEffect, useState } from 'react';
import { startsSoon } from '@versesverse/core';

/**
 * WHEN A SESSION IS, in the reader's own words.
 *
 * Extracted from the sessions panel because the Hall page shows the same thing, and this is the
 * half worth having in one place: the server stores an INSTANT, and `toLocaleString` with no
 * explicit zone renders it wherever the reader is standing. Getting that wrong produces no error
 * anybody sees — just a study group in the wrong evening and an empty room.
 *
 * A time we cannot parse says so rather than printing "Invalid Date" at a reader.
 */
export function sessionWhen(startsAt: string, lang: string, unknown: string): string {
  return readableTime(startsAt, lang, unknown, {
    weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}

/**
 * A TIME OFF THE WIRE, IN THE READER'S OWN LANGUAGE — or a sentence saying it could not be read.
 *
 * `new Date(x).toLocaleString(...)` renders **"Invalid Date"** for anything it cannot parse: an
 * English string in the middle of a Hebrew page, and a lie about the row it sits in — a
 * moderator's act appearing to have happened at a non-time. This guard was written here for a
 * study's hour and **the two audit logs each wrote the formatting again without it**, which is
 * the shape of half the near-misses in `COMMUNICATION.md §0`: a rule that exists once and is
 * copied everywhere except the part that made it a rule.
 *
 * The FORMAT stays with the caller and the guard does not. A log row wants a day and an hour, a
 * study wants a weekday too, and a queue at a door wants to be scannable — those are three
 * decisions about three lists. **Whether a time can be read at all is not a decision.**
 *
 * The language rather than the browser's locale, for the reason the Hall page states: a reader
 * who chose Hebrew has chosen it for the dates as well.
 */
export function readableTime(
  iso: string,
  lang: string,
  unknown: string,
  how: Intl.DateTimeFormatOptions,
): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return unknown;
  return at.toLocaleString(lang, how);
}

/**
 * WHEN A SESSION IS, AS A READER NEEDS IT — the date, or the distance when the distance is the fact.
 *
 * Two panels draw the same session: the node's strip, and the Hall page a leader links from a
 * YouTube description. The strip learnt to say "in 12 minutes"; the Hall page went on printing an
 * instant, so somebody arriving from that link at ten to eight was given the date of an evening
 * they were already standing in. A branch written twice is a branch that gets fixed once, so it
 * lives here, beside the formatting it replaces.
 *
 * The PHRASES come from the caller, because they belong to the dictionary and exist in three
 * languages. The WINDOW and the rounding come from core, so no client can disagree with another
 * about how near "near" is.
 */
export function sessionWhenText(
  startsAt: string,
  lang: string,
  say: {
    unknown: string;
    now: string;
    inMinutes: (count: number) => string;
    /**
     * HOW LONG IT LASTS — the promise a leader made and the audience was never shown.
     *
     * `durationMinutes` decides how long the room is HELD and when the study is considered over,
     * so it is not cosmetic; it was simply invisible. A reader deciding whether to come to a study
     * that starts in ten minutes is deciding about the whole of it, and "20:00" alone does not say
     * whether that is half an hour or two.
     *
     * Required rather than optional, so the Hall page cannot go on omitting it: this function
     * exists because *"a branch written twice is a branch that gets fixed once"*, and an optional
     * argument would have recreated exactly that.
     */
    lasts: (minutes: number) => string;
  },
  at: Date = new Date(),
  durationMinutes?: number,
): string {
  const soon = startsSoon(startsAt, at);
  const when = soon === null
    ? sessionWhen(startsAt, lang, say.unknown)
    : soon === 0 ? say.now : say.inMinutes(soon);
  /* Absent for a study scheduled before the field existed — the time alone is what it always was. */
  return durationMinutes === undefined ? when : `${when} · ${say.lasts(durationMinutes)}`;
}

/**
 * A clock that moves, once a minute.
 *
 * Neither panel is otherwise time-dependent, so neither re-renders on its own: a reader who leaves
 * a tab open would watch a countdown that never counted, which is worse than the date it replaced.
 * A hook rather than a copy of the same `setInterval` in each panel, for the reason above.
 */
export function useMinuteTick(): number {
  const [tick, setTick] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setTick(Date.now()), 60_000);
    return () => clearInterval(timer);
  }, []);
  return tick;
}
